//-- Copyright (c) 2021 TMT Insights Group, LLC
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../_utils/axios';
import { CustomerProfile, ProfileState, Workflow } from './slice_types';
import { set } from 'lodash';

const initialState: ProfileState = {
  workflows: {},
  profiles: {},
  allProfiles: [],
  deletedProfiles: [],
  showDeletedProfilesInJobsFilter: false
};

// THUNKS
// used in Order Placement
export const getProfiles = createAsyncThunk(
  'profile/getProfiles',
  async (params: { customer: string; order_on?: string; direction?: string; show_hidden?: boolean }) => {
    const { customer, order_on, direction, show_hidden } = params;
    return axios
      .post(`/core/profile_templates/search/?page_size=999`, { customer_filter: [customer], order_on, direction, show_hidden })
      .then((res): { customer: string; profiles: CustomerProfile[] } => ({ customer, profiles: res.data?.data || [] }));
  }
);

export const getDeletedProfiles = createAsyncThunk('profile/getDeletedProfiles', async () => {
  return axios
    .post(`/core/profile_templates/search/?page_size=999`, { search: '(Deleted:', customer_filter: [], show_deleted: true })
    .then(async (res): { profiles: CustomerProfile[] } => {
      const origProfiles = res.data?.data || [];
      if (res.data?.pagination && res.data.pagination.total <= res.data.pagination.page_size) return { profiles: origProfiles };

      const totalProfiles = res.data.pagination.total;
      const pageSize = res.data.pagination.page_size;
      const totalPages = Math.ceil(totalProfiles / pageSize);

      const pageRequests = [];
      for (let page = 1; page + 1 <= totalPages; page++) {
        pageRequests.push(
          axios.post(`/core/profile_templates/search/?page_size=${pageSize}&page_num=${page}`, {
            search: '(Deleted:',
            customer_filter: [],
            show_hidden: true
          })
        );
      }

      const responses = await Promise.all(pageRequests);

      const allProfiles = responses.reduce((acc, response) => {
        const profiles = response.data?.data || [];
        return acc.concat(profiles);
      }, origProfiles);

      return { profiles: allProfiles };
    });
});

export const getMultipleProfiles = createAsyncThunk('profile/getMultipleProfiles', async (customers: string[]) => {
  return axios.post(`/core/profile_templates/search/?page_size=999`, { customer_filter: customers }).then(({ data }) => {
    if (!data?.data) return null;
    const customerProfiles = data.data as CustomerProfile[];
    const profilesArr = customerProfiles.reduce((acc: Record<string, CustomerProfile[]>, profile: CustomerProfile) => {
      if (!profile.customer_name) return acc;
      const customerName = profile.customer_name as string;
      if (!acc[customerName]) {
        acc[customerName] = [profile];
        return acc;
      }

      acc[customerName].push(profile);
      return acc;
    }, {});
    return profilesArr;
  });
});

export const getAllProfiles = createAsyncThunk('profile/getAllProfiles', async () => {
  return axios.post(`/core/profile_templates/search/?page_size=999`, { customer_filter: [] }).then(async (res): { profiles: CustomerProfile[] } => {
    const origProfiles = res.data?.data || [];
    if (res.data?.pagination && res.data.pagination.total <= res.data.pagination.page_size) return { profiles: origProfiles };

    const totalProfiles = res.data.pagination.total;
    const pageSize = res.data.pagination.page_size;
    const totalPages = Math.ceil(totalProfiles / pageSize);

    const pageRequests = [];
    for (let page = 1; page + 1 <= totalPages; page++) {
      pageRequests.push(axios.post(`/core/profile_templates/search/?page_size=${pageSize}&page_num=${page}`, { customer_filter: [] }));
    }

    const responses = await Promise.all(pageRequests);

    const allProfiles = responses.reduce((acc, response) => {
      const profiles = response.data?.data || [];
      return acc.concat(profiles);
    }, origProfiles);

    return { profiles: allProfiles };
  });
});

export const getWorkflows = createAsyncThunk('profile/getWorkflows', async () => {
  // we shouldn't have many workflows for any client, limiting to 99 for now
  return axios
    .post(`/core/workflows/search/?page_size=99`)
    .then((res): Workflow[] => res.data?.data || [])
    .catch(() => []);
});

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearProfiles: (state) => {
      state.profiles = {};
    },
    setShowDeletedProfilesInJobsFilter: (state, action) => {
      state.showDeletedProfilesInJobsFilter = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getAllProfiles.fulfilled, (state, { payload }) => {
      state.allProfiles = payload.profiles;
    });
    builder.addCase(getProfiles.fulfilled, (state, { payload }) => {
      state.profiles[(payload as any).customer] = (payload as any).profiles.map((p: CustomerProfile) => ({
        ...p,
        label: p.name,
        value: p.slug,
        data: { ...p.data, workflow_type: p.data.workflow_type || 'version_based' }
      }));
    });
    builder.addCase(getMultipleProfiles.fulfilled, (state, { payload }) => {
      if (!payload) return;
      for (const prop in payload) {
        const profiles = payload[prop];
        state.profiles[prop] = profiles.map((p: CustomerProfile) => ({
          ...p,
          label: p.name,
          value: p.slug,
          data: { ...p.data, workflow_type: p.data.workflow_type || 'version_based' }
        }));
      }
    });
    builder.addCase(getDeletedProfiles.fulfilled, (state, { payload }) => {
      state.deletedProfiles = payload.profiles;
    });

    builder.addCase(getWorkflows.fulfilled, (state, { payload }) => {
      payload.forEach((workflow: Workflow) => {
        state.workflows[workflow.name] = workflow;
      });
    });
  }
});

export const { clearProfiles, setShowDeletedProfilesInJobsFilter } = profileSlice.actions;

export default profileSlice.reducer;
